import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Plus, Pencil, Users, Info, Search, UserX, UserCheck, UserPlus, Phone, Smartphone, ArrowRightLeft, Briefcase, X, Building2, ChevronDown, Eye, User, FileSpreadsheet, KeyRound } from "lucide-react";
import { useNavigate } from "react-router-dom";
import * as api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../ui/dialog";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../ui/tooltip";
import { EmptyState } from "../shared/EmptyState";
import { UserAvatar } from "../shared/UserAvatar";
import { ROLES } from "../../lib/utils";
import { toast } from "sonner";
import { UserPermissionsDialog } from "../access/UserPermissionsDialog";
import { EntraSyncButton } from "../shared/EntraSyncButton";
import { LeftCompanyBadge } from "../shared/LeftCompany";
import { hasLeftCompany } from "../../lib/utils";

function normalizeString(str) {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

function isPlaceholderEmail(email) {
    return !email || email.includes("@no-email.local");
}

export function AdminUsers() {
    const { users, departments, currentUser, refreshUsers, impersonating, startImpersonation } = useApp();
    const navigate = useNavigate();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [permUser, setPermUser] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [deptFilter, setDeptFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("active");
    const [typeFilter, setTypeFilter] = useState("all");
    const [deactivateTarget, setDeactivateTarget] = useState(null);
    const [positions, setPositions] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkDropdown, setBulkDropdown] = useState(null); // "position-add" | "position-remove" | "dept" | null
    const [formData, setFormData] = useState({
        email: "", first_name: "", last_name: "", department_id: "",
        secondary_department_ids: [], role: "user",
        phone_fixed: "", phone_mobile: "", employee_id: "", user_type: "full", position_ids: [],
    });

    // Transfer dialog
    const [transferOpen, setTransferOpen] = useState(false);
    const [transferUser, setTransferUser] = useState(null);
    const [transferToDeptId, setTransferToDeptId] = useState("");
    const [transferToDeptName, setTransferToDeptName] = useState("");
    const [transferReason, setTransferReason] = useState("");
    const [transferDate, setTransferDate] = useState(new Date().toISOString().slice(0, 10));
    const [transferFreeText, setTransferFreeText] = useState(false);
    const [transferSubmitting, setTransferSubmitting] = useState(false);

    useEffect(() => { api.getPositions().then(setPositions).catch(() => {}); }, []);

    const filteredUsers = useMemo(() => {
        let list = users;
        if (statusFilter === "active") list = list.filter((u) => u.is_active !== false);
        else if (statusFilter === "inactive") list = list.filter((u) => u.is_active === false);
        else if (statusFilter === "left") list = list.filter((u) => hasLeftCompany(u));
        if (typeFilter === "external") list = list.filter((u) => u.user_type === "external");
        else if (typeFilter === "full") list = list.filter((u) => u.user_type !== "external");
        if (deptFilter && deptFilter !== "all") {
            list = list.filter((u) => u.department_id === deptFilter || (u.secondary_department_ids || []).includes(deptFilter));
        }
        if (searchQuery.trim().length > 0) {
            const q = normalizeString(searchQuery);
            list = list.filter((u) => {
                const deptName = departments.find((d) => d.id === u.department_id)?.name || "";
                return normalizeString(`${u.first_name} ${u.last_name} ${u.email} ${deptName}`).includes(q);
            });
        }
        return list;
    }, [users, searchQuery, deptFilter, statusFilter, typeFilter, departments]);

    // Clear selection when filters change
    useEffect(() => { setSelectedIds(new Set()); }, [searchQuery, deptFilter, statusFilter, typeFilter]);

    const handleOpenDialog = (item = null, isExternal = false) => {
        if (item) {
            setEditing(item);
            setFormData({
                email: item.email, first_name: item.first_name, last_name: item.last_name,
                department_id: item.department_id || "", secondary_department_ids: item.secondary_department_ids || [],
                role: item.role, phone_fixed: item.phone_fixed || "", phone_mobile: item.phone_mobile || "",
                employee_id: item.employee_id || "",
                user_type: item.user_type || "full", position_ids: item.position_ids || [],
            });
        } else {
            setEditing(null);
            setFormData({
                email: "", first_name: "", last_name: "", department_id: "",
                secondary_department_ids: [], role: "user",
                phone_fixed: "", phone_mobile: "", employee_id: "",
                user_type: isExternal ? "external" : "full", position_ids: [],
            });
        }
        setDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            const payload = { ...formData };
            if (payload.user_type === "external" && !payload.email.trim()) payload.email = "";
            if (editing) {
                await api.updateUser(editing.id, payload);
                toast.success("Utilisateur mis à jour");
            } else {
                await api.createUser(payload);
                toast.success(payload.user_type === "external" ? "Contact externe créé" : "Utilisateur créé");
            }
            setDialogOpen(false);
            refreshUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Erreur lors de la sauvegarde");
        }
    };

    const handleToggleActive = async (user, activate) => {
        if (!activate) { setDeactivateTarget(user); return; }
        try {
            await api.updateUser(user.id, { ...user, is_active: true });
            toast.success("Utilisateur réactivé");
            refreshUsers();
        } catch { toast.error("Erreur lors de la réactivation"); }
    };

    const confirmDeactivate = async () => {
        if (!deactivateTarget) return;
        try {
            await api.updateUser(deactivateTarget.id, { ...deactivateTarget, is_active: false });
            toast.success("Utilisateur désactivé");
            refreshUsers();
        } catch { toast.error("Erreur lors de la désactivation"); }
        setDeactivateTarget(null);
    };

    const getRoleLabel = (roleId) => ROLES.find((r) => r.id === roleId)?.label || roleId;
    const getManagerFromDepartment = (deptId) => {
        const dept = departments.find((d) => d.id === deptId);
        if (!dept?.manager_id) return null;
        return users.find((u) => u.id === dept.manager_id);
    };
    const getDeptManagedBy = (userId) => departments.find((d) => d.manager_id === userId);
    const toggleSecondary = (deptId) => {
        setFormData((prev) => {
            const ids = prev.secondary_department_ids || [];
            return { ...prev, secondary_department_ids: ids.includes(deptId) ? ids.filter((d) => d !== deptId) : [...ids, deptId] };
        });
    };
    const togglePosition = (posId) => {
        setFormData((prev) => {
            const ids = prev.position_ids || [];
            return { ...prev, position_ids: ids.includes(posId) ? ids.filter((p) => p !== posId) : [...ids, posId] };
        });
    };
    const getPositionNames = useCallback((positionIds) => {
        if (!positionIds?.length) return [];
        return positionIds.map((pid) => positions.find((p) => p.id === pid)?.name).filter(Boolean);
    }, [positions]);

    const secondaryDeptOptions = useMemo(() => departments.filter((d) => d.id !== formData.department_id), [departments, formData.department_id]);
    const isFormExternal = formData.user_type === "external";
    const canSave = formData.first_name && formData.last_name && (isFormExternal || formData.email);

    // ---- Bulk selection ----
    const filteredIds = useMemo(() => new Set(filteredUsers.map((u) => u.id)), [filteredUsers]);
    const allSelected = filteredUsers.length > 0 && filteredUsers.every((u) => selectedIds.has(u.id));
    const toggleSelectAll = () => {
        if (allSelected) { setSelectedIds(new Set()); }
        else { setSelectedIds(new Set(filteredUsers.map((u) => u.id))); }
    };
    const toggleSelectUser = (userId) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(userId)) next.delete(userId); else next.add(userId);
            return next;
        });
    };
    const selectionCount = [...selectedIds].filter((id) => filteredIds.has(id)).length;
    const selectedUsers = useMemo(() => users.filter((u) => selectedIds.has(u.id)), [users, selectedIds]);

    // Bulk assign position
    const handleBulkAssignPosition = async (posId) => {
        setBulkDropdown(null);
        const pos = positions.find((p) => p.id === posId);
        if (!pos) return;
        if (!window.confirm(`Attribuer le poste "${pos.name}" à ${selectionCount} utilisateur(s) ?`)) return;
        try {
            const result = await api.bulkAssignPosition({ user_ids: [...selectedIds], position_id: posId, action: "add" });
            toast.success(`Poste "${pos.name}" attribué à ${result.updated} utilisateur(s)`);
            setSelectedIds(new Set());
            refreshUsers();
        } catch { toast.error("Erreur lors de l'attribution"); }
    };

    const handleBulkRemovePosition = async (posId) => {
        setBulkDropdown(null);
        const pos = positions.find((p) => p.id === posId);
        if (!pos) return;
        if (!window.confirm(`Retirer le poste "${pos.name}" de ${selectionCount} utilisateur(s) ?`)) return;
        try {
            const result = await api.bulkAssignPosition({ user_ids: [...selectedIds], position_id: posId, action: "remove" });
            toast.success(`Poste "${pos.name}" retiré de ${result.updated} utilisateur(s)`);
            setSelectedIds(new Set());
            refreshUsers();
        } catch { toast.error("Erreur lors du retrait"); }
    };

    const handleBulkChangeDept = async (deptId) => {
        setBulkDropdown(null);
        const dept = departments.find((d) => d.id === deptId);
        if (!dept) return;
        if (!window.confirm(`Déplacer ${selectionCount} utilisateur(s) vers "${dept.name}" ?`)) return;
        try {
            const result = await api.bulkUpdateUsers({ user_ids: [...selectedIds], updates: { department_id: deptId } });
            toast.success(`${result.updated} utilisateur(s) déplacé(s) vers "${dept.name}"`);
            setSelectedIds(new Set());
            refreshUsers();
        } catch { toast.error("Erreur lors du déplacement"); }
    };

    const handleBulkDeactivate = async () => {
        setBulkDropdown(null);
        if (!window.confirm(`Désactiver ${selectionCount} utilisateur(s) ? Ils ne pourront plus se connecter.`)) return;
        try {
            const result = await api.bulkUpdateUsers({ user_ids: [...selectedIds], updates: { is_active: false } });
            toast.success(`${result.updated} utilisateur(s) désactivé(s)`);
            setSelectedIds(new Set());
            refreshUsers();
        } catch { toast.error("Erreur lors de la désactivation"); }
    };

    // Positions present among selected users (for remove dropdown)
    const selectedPositionCounts = useMemo(() => {
        const counts = {};
        selectedUsers.forEach((u) => {
            (u.position_ids || []).forEach((pid) => { counts[pid] = (counts[pid] || 0) + 1; });
        });
        return counts;
    }, [selectedUsers]);

    // Transfer
    const openTransferDialog = (user) => {
        setTransferUser(user);
        setTransferToDeptId(""); setTransferToDeptName(""); setTransferReason("");
        setTransferDate(new Date().toISOString().slice(0, 10)); setTransferFreeText(false);
        setTransferOpen(true);
    };
    const handleTransferSubmit = async () => {
        if (!transferUser || !transferToDeptName) return;
        setTransferSubmitting(true);
        try {
            const empName = `${transferUser.first_name} ${transferUser.last_name}`;
            const fromDept = departments.find((d) => d.id === transferUser.department_id);
            const fromName = fromDept?.name || "N/A";
            const result = await api.createTicket({
                type: "department_transfer", priority: "medium",
                title: `Transfert de département — ${empName} : ${fromName} → ${transferToDeptName}`,
                description: `Demande de transfert de ${empName} du département ${fromName} vers ${transferToDeptName}.${transferReason ? `\nMotif : ${transferReason}.` : ""}\nDate effective : ${transferDate}.`,
                requester_id: currentUser.id, beneficiary_id: transferUser.id,
                dynamic_fields: {
                    employee_id: transferUser.id, employee_name: empName,
                    from_department_id: fromDept?.id || "", from_department_name: fromName,
                    to_department_id: transferToDeptId, to_department_name: transferToDeptName,
                    transfer_reason: transferReason, effective_date: transferDate,
                },
            });
            toast.success(`Ticket de transfert créé : #${result.ticket_number}`);
            setTransferOpen(false); navigate(`/tickets/${result.id}`);
        } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
        finally { setTransferSubmitting(false); }
    };
    const handleSelectTransferDept = (deptId) => {
        if (!deptId || deptId === "none") { setTransferToDeptId(""); setTransferToDeptName(""); return; }
        const dept = departments.find((d) => d.id === deptId);
        setTransferToDeptId(dept?.id || ""); setTransferToDeptName(dept?.name || "");
    };

    return (
        <div className="page-container" data-testid="admin-users">
            <div className="page-header flex items-center justify-between">
                <div>
                    <h1 className="page-title">Utilisateurs</h1>
                    <p className="page-subtitle">{users.length} utilisateurs</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={async () => {
                        try {
                            const params = {};
                            if (deptFilter && deptFilter !== "all") params.department_id = deptFilter;
                            const response = await api.exportUsersExcel(params);
                            const url = URL.createObjectURL(response.data);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `export_utilisateurs_${new Date().toISOString().slice(0, 10)}.xlsx`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                        } catch { /* toast handled by interceptor */ }
                    }} data-testid="export-users-btn">
                        <FileSpreadsheet className="w-4 h-4 mr-1" />Exporter
                    </Button>
                    <EntraSyncButton size="default" onDone={refreshUsers} />
                    <Button variant="outline" onClick={() => handleOpenDialog(null, true)} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="add-external-btn">
                        <UserPlus className="w-4 h-4 mr-2" />+ Contact externe
                    </Button>
                    <Button onClick={() => handleOpenDialog()} data-testid="add-user-btn">
                        <Plus className="w-4 h-4 mr-2" />Ajouter
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="mb-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3" data-testid="users-filter-bar">
                <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Rechercher un utilisateur..." className="pl-9" data-testid="users-search-input" />
                </div>
                <Select value={deptFilter} onValueChange={setDeptFilter}>
                    <SelectTrigger className="w-full sm:w-56" data-testid="users-dept-filter"><SelectValue placeholder="Tous les départements" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tous les départements</SelectItem>
                        {departments.map((d) => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}
                    </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full sm:w-36" data-testid="users-status-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        <SelectItem value="active">Actifs</SelectItem>
                        <SelectItem value="inactive">Inactifs</SelectItem>
                        <SelectItem value="left" data-testid="status-filter-left">Anciens collaborateurs</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="w-full sm:w-40" data-testid="users-type-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tous les types</SelectItem>
                        <SelectItem value="full">Complets</SelectItem>
                        <SelectItem value="external">Externes</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Bulk action bar */}
            {selectionCount > 0 && (
                <div className="mb-3 flex items-center gap-2 p-3 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="bulk-action-bar">
                    <span className="text-sm font-semibold text-indigo-800">{selectionCount} sélectionné(s)</span>
                    <div className="flex-1" />
                    {/* Assign position */}
                    <div className="relative">
                        <Button size="sm" variant="outline" onClick={() => setBulkDropdown(bulkDropdown === "position-add" ? null : "position-add")} data-testid="bulk-assign-position-btn">
                            <Briefcase className="w-3 h-3 mr-1" />Attribuer un poste <ChevronDown className="w-3 h-3 ml-1" />
                        </Button>
                        {bulkDropdown === "position-add" && positions.length > 0 && (
                            <div className="absolute z-50 mt-1 right-0 w-64 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="bulk-position-add-dropdown">
                                {positions.map((p) => (
                                    <button key={p.id} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b last:border-b-0" onClick={() => handleBulkAssignPosition(p.id)} data-testid={`bulk-add-pos-${p.id}`}>
                                        {p.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {/* Remove position */}
                    <div className="relative">
                        <Button size="sm" variant="outline" onClick={() => setBulkDropdown(bulkDropdown === "position-remove" ? null : "position-remove")} data-testid="bulk-remove-position-btn">
                            <X className="w-3 h-3 mr-1" />Retirer un poste <ChevronDown className="w-3 h-3 ml-1" />
                        </Button>
                        {bulkDropdown === "position-remove" && (
                            <div className="absolute z-50 mt-1 right-0 w-64 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="bulk-position-remove-dropdown">
                                {Object.keys(selectedPositionCounts).length === 0 ? (
                                    <p className="px-3 py-2 text-sm text-gray-400">Aucun poste commun</p>
                                ) : (
                                    Object.entries(selectedPositionCounts).map(([pid, cnt]) => {
                                        const pos = positions.find((p) => p.id === pid);
                                        return pos ? (
                                            <button key={pid} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b last:border-b-0 flex justify-between" onClick={() => handleBulkRemovePosition(pid)} data-testid={`bulk-remove-pos-${pid}`}>
                                                <span>{pos.name}</span>
                                                <span className="text-xs text-gray-400">{cnt}/{selectionCount}</span>
                                            </button>
                                        ) : null;
                                    })
                                )}
                            </div>
                        )}
                    </div>
                    {/* Change department */}
                    <div className="relative">
                        <Button size="sm" variant="outline" onClick={() => setBulkDropdown(bulkDropdown === "dept" ? null : "dept")} data-testid="bulk-change-dept-btn">
                            <Building2 className="w-3 h-3 mr-1" />Changer département <ChevronDown className="w-3 h-3 ml-1" />
                        </Button>
                        {bulkDropdown === "dept" && (
                            <div className="absolute z-50 mt-1 right-0 w-64 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="bulk-dept-dropdown">
                                {departments.map((d) => (
                                    <button key={d.id} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b last:border-b-0" onClick={() => handleBulkChangeDept(d.id)} data-testid={`bulk-dept-${d.id}`}>
                                        {d.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <Button size="sm" variant="destructive" onClick={handleBulkDeactivate} data-testid="bulk-deactivate-btn" className="bg-red-600 hover:bg-red-700 text-white">
                        <UserX className="w-3 h-3 mr-1" />Désactiver
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} data-testid="bulk-cancel-btn">Annuler</Button>
                </div>
            )}

            <p className="text-xs text-gray-500 mb-3" data-testid="users-result-count">{filteredUsers.length} utilisateur(s) trouvé(s)</p>

            {filteredUsers.length === 0 ? (
                <div className="card"><EmptyState icon={Users} title="Aucun utilisateur" description={searchQuery || deptFilter !== "all" || typeFilter !== "all" ? "Aucun résultat pour ces filtres" : "Ajoutez des utilisateurs"} /></div>
            ) : (
                <div className="card">
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th className="w-10"><Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} data-testid="select-all-users" /></th>
                                    <th>Utilisateur</th>
                                    <th>Email</th>
                                    <th>Département</th>
                                    <th>Poste(s)</th>
                                    <th>Rôle</th>
                                    <th>Source</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map((user) => {
                                    const dept = departments.find((d) => d.id === user.department_id);
                                    const isInactive = user.is_active === false;
                                    const isExternal = user.user_type === "external";
                                    const secDepts = (user.secondary_department_ids || []).map((sid) => departments.find((d) => d.id === sid)?.name).filter(Boolean);
                                    const displayEmail = isPlaceholderEmail(user.email) ? null : user.email;
                                    const posNames = getPositionNames(user.position_ids);
                                    const isSelected = selectedIds.has(user.id);

                                    return (
                                        <tr key={user.id} className={`${isInactive ? "opacity-50 bg-gray-50" : ""} ${isSelected ? "bg-indigo-50/50" : ""}`} data-testid={`user-row-${user.id}`}>
                                            <td><Checkbox checked={isSelected} onCheckedChange={() => toggleSelectUser(user.id)} data-testid={`select-user-${user.id}`} /></td>
                                            <td>
                                                <div className="flex items-center gap-3">
                                                    <UserAvatar user={user} size="md" />
                                                    <div>
                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                            <span className="font-medium text-sm">{user.first_name} {user.last_name}</span>
                                                            {user.employee_id && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono bg-gray-100 text-gray-600" title="N° employé" data-testid={`employee-id-${user.id}`}>N° {user.employee_id}</span>}
                                                            {isExternal && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700" data-testid={`external-badge-${user.id}`}>EXT</span>}
                                                            {hasLeftCompany(user) ? <LeftCompanyBadge user={user} size="xs" /> : isInactive && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700" data-testid={`inactive-badge-${user.id}`}>Inactif</span>}
                                                        </div>
                                                        {(user.phone_fixed || user.phone_mobile) && (
                                                            <div className="flex items-center gap-3 mt-0.5">
                                                                {user.phone_fixed && <span className="flex items-center gap-0.5 text-[10px] text-gray-400"><Phone className="w-2.5 h-2.5" />{user.phone_fixed}</span>}
                                                                {user.phone_mobile && <span className="flex items-center gap-0.5 text-[10px] text-gray-400"><Smartphone className="w-2.5 h-2.5" />{user.phone_mobile}</span>}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="text-gray-500 text-sm">{displayEmail || <span className="text-gray-300 italic text-xs">—</span>}</td>
                                            <td>
                                                <div>
                                                    <span className="text-sm">{dept?.name || "—"}</span>
                                                    {secDepts.length > 0 && <span className="block text-[10px] text-gray-400 mt-0.5">+ {secDepts.join(", ")}</span>}
                                                </div>
                                            </td>
                                            <td>
                                                {posNames.length > 0 ? (
                                                    <div className="flex flex-wrap gap-1">
                                                        {posNames.map((name) => (
                                                            <span key={name} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-700" data-testid={`position-badge-${name}`}>{name}</span>
                                                        ))}
                                                    </div>
                                                ) : <span className="text-gray-300 text-xs">—</span>}
                                            </td>
                                            <td>
                                                <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                                                    user.role === "admin" ? "bg-purple-100 text-purple-700"
                                                    : user.role === "agent" ? "bg-blue-100 text-blue-700"
                                                    : user.role === "approver" ? "bg-orange-100 text-orange-700"
                                                    : "bg-gray-100 text-gray-700"}`}>
                                                    {getRoleLabel(user.role)}
                                                </span>
                                            </td>
                                            <td>
                                                {isExternal ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">EXT</span>
                                                : user.source === "sso" ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">MS365</span>
                                                : user.is_synced_from_ad ? (
                                                    <TooltipProvider><Tooltip><TooltipTrigger><span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">AD</span></TooltipTrigger>
                                                    <TooltipContent><p>Synchronisé depuis Active Directory</p>{user.ad_sam_account_name && <p className="text-xs text-gray-400">{user.ad_sam_account_name}</p>}</TooltipContent></Tooltip></TooltipProvider>
                                                ) : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">Local</span>}
                                            </td>
                                            <td>
                                                <div className="flex items-center gap-0.5 justify-end">
                                                    {!impersonating && currentUser?.role === "admin" && user.role !== "admin" && (
                                                        <Button variant="ghost" size="sm" onClick={() => { startImpersonation(user); navigate("/"); }} title="Observer" data-testid={`observe-user-${user.id}`}><Eye className="w-3.5 h-3.5 text-amber-500" /></Button>
                                                    )}
                                                    <Button variant="ghost" size="sm" onClick={() => navigate(`/users/${user.id}/profile`)} title="Profil" data-testid={`profile-user-${user.id}`}><User className="w-3.5 h-3.5 text-teal-500" /></Button>
                                                    <Button variant="ghost" size="sm" onClick={() => openTransferDialog(user)} title="Transférer" data-testid={`transfer-user-${user.id}`}><ArrowRightLeft className="w-3.5 h-3.5 text-gray-400" /></Button>
                                                    <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(user)} data-testid={`edit-user-${user.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                                                    {!isExternal && (
                                                        <Button variant="ghost" size="sm" onClick={() => setPermUser(user)} title="Accès aux fonctionnalités" data-testid={`access-user-${user.id}`}>
                                                            <KeyRound className={`w-3.5 h-3.5 ${(user.permissions || []).length > 0 ? "text-teal-600" : "text-gray-400"}`} />
                                                        </Button>
                                                    )}
                                                    {isInactive ? (
                                                        <Button variant="ghost" size="sm" onClick={() => handleToggleActive(user, true)} data-testid={`activate-user-${user.id}`} title="Réactiver"><UserCheck className="w-3.5 h-3.5 text-green-600" /></Button>
                                                    ) : (
                                                        <Button variant="ghost" size="sm" onClick={() => handleToggleActive(user, false)} data-testid={`deactivate-user-${user.id}`} title="Désactiver"><UserX className="w-3.5 h-3.5 text-red-500" /></Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <UserPermissionsDialog user={permUser} open={!!permUser} onClose={() => setPermUser(null)} />

            {/* EDIT/CREATE DIALOG */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Modifier l'utilisateur" : isFormExternal ? "Ajouter un contact externe" : "Ajouter un utilisateur"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {isFormExternal && (
                            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                                <UserPlus className="w-4 h-4 text-amber-600" />
                                <span className="text-xs text-amber-800 font-medium">Contact externe — l'email est optionnel.</span>
                            </div>
                        )}
                        {/* Type indicator (read-only when editing) */}
                        {editing && (
                            <div className="flex items-center gap-2 text-xs">
                                <span className="text-gray-500">Type :</span>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded font-medium ${isFormExternal ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-700"}`}>
                                    {isFormExternal ? "EXT" : "Complet"}
                                </span>
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label className="form-label">Prénom *</Label><Input value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} data-testid="user-firstname" /></div>
                            <div><Label className="form-label">Nom *</Label><Input value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} data-testid="user-lastname" /></div>
                        </div>
                        <div>
                            <Label className="form-label">Email {isFormExternal ? "(optionnel)" : "*"}</Label>
                            <Input type="email" value={isPlaceholderEmail(formData.email) ? "" : formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder={isFormExternal ? "Optionnel" : "email@example.com"} data-testid="user-email" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label className="form-label">Tél. fixe</Label><Input value={formData.phone_fixed} onChange={(e) => setFormData({ ...formData, phone_fixed: e.target.value })} placeholder="+41 ..." data-testid="user-phone-fixed" /></div>
                            <div><Label className="form-label">Tél. mobile</Label><Input value={formData.phone_mobile} onChange={(e) => setFormData({ ...formData, phone_mobile: e.target.value })} placeholder="+41 ..." data-testid="user-phone-mobile" /></div>
                        </div>
                        <div>
                            <Label className="form-label">N° d'employé <span className="text-gray-400 font-normal">(synchronisé depuis Microsoft 365 — employeeId)</span></Label>
                            <Input value={formData.employee_id} onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })} placeholder="ex. 10452" className="font-mono" data-testid="user-employee-id" />
                        </div>
                        {editing?.department_name_ms365 && (
                            <div><Label className="form-label text-gray-400">Département MS365 (lecture seule)</Label><Input value={editing.department_name_ms365} disabled className="bg-gray-50 text-gray-500" data-testid="user-dept-ms365" /></div>
                        )}
                        <div>
                            <Label className="form-label">Département principal</Label>
                            <Select value={formData.department_id} onValueChange={(v) => {
                                const newSec = (formData.secondary_department_ids || []).filter((d) => d !== v);
                                setFormData({ ...formData, department_id: v, secondary_department_ids: newSec });
                            }}>
                                <SelectTrigger data-testid="user-department"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                <SelectContent>
                                    {departments.map((d) => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </div>
                        {secondaryDeptOptions.length > 0 && (
                            <div>
                                <Label className="form-label">Départements secondaires</Label>
                                <div className="border rounded-md p-3 max-h-32 overflow-y-auto space-y-2" data-testid="user-secondary-depts">
                                    {secondaryDeptOptions.map((d) => (
                                        <label key={d.id} className="flex items-center gap-2 cursor-pointer text-sm">
                                            <Checkbox checked={(formData.secondary_department_ids || []).includes(d.id)} onCheckedChange={() => toggleSecondary(d.id)} data-testid={`secondary-dept-${d.id}`} />
                                            {d.name}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                        {!isFormExternal && (
                            <div>
                                <Label className="form-label">Rôle</Label>
                                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                                    <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                                    <SelectContent>{ROLES.map((r) => (<SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>))}</SelectContent>
                                </Select>
                            </div>
                        )}
                        {positions.length > 0 && (
                            <div>
                                <Label className="form-label">Poste(s) / Fonction(s)</Label>
                                <div className="border rounded-md p-3 max-h-32 overflow-y-auto space-y-2" data-testid="user-positions">
                                    {positions.map((pos) => (
                                        <label key={pos.id} className="flex items-center gap-2 cursor-pointer text-sm">
                                            <Checkbox checked={(formData.position_ids || []).includes(pos.id)} onCheckedChange={() => togglePosition(pos.id)} data-testid={`position-${pos.id}`} />
                                            <Briefcase className="w-3 h-3 text-gray-400" />{pos.name}
                                        </label>
                                    ))}
                                </div>
                                {formData.position_ids?.length > 0 && <p className="text-xs text-gray-500 mt-1">{getPositionNames(formData.position_ids).join(", ")}</p>}
                            </div>
                        )}
                        {/* Active toggle when editing */}
                        {editing && (
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                                <div>
                                    <Label className="form-label mb-0">Statut du compte</Label>
                                    <p className="text-xs text-gray-500">{editing.is_active !== false ? "Ce compte est actif" : "Ce compte est désactivé"}</p>
                                </div>
                                <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${editing.is_active !== false ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                                    {editing.is_active !== false ? "Actif" : "Inactif"}
                                </span>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                        <Button onClick={handleSave} disabled={!canSave} data-testid="save-user-btn">{editing ? "Enregistrer" : "Créer"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* DEACTIVATE CONFIRMATION */}
            {deactivateTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="deactivate-confirm-overlay">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-[95vw] overflow-hidden">
                        <div className="px-5 py-4 border-b bg-red-50">
                            <h3 className="text-sm font-semibold text-red-900">Désactiver {deactivateTarget.first_name} {deactivateTarget.last_name} ?</h3>
                        </div>
                        <div className="px-5 py-4 space-y-2">
                            <p className="text-sm text-gray-700">Cette personne ne pourra plus se connecter au système.</p>
                            {(() => {
                                const managedDept = getDeptManagedBy(deactivateTarget.id);
                                return managedDept ? (
                                    <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                        <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                                        <p className="text-xs text-amber-800">Attention : responsable du département <strong>{managedDept.name}</strong>.</p>
                                    </div>
                                ) : null;
                            })()}
                        </div>
                        <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => setDeactivateTarget(null)} data-testid="deactivate-cancel-btn">Annuler</Button>
                            <Button size="sm" variant="destructive" onClick={confirmDeactivate} data-testid="deactivate-confirm-btn" className="bg-red-600 hover:bg-red-700 text-white">Désactiver</Button>
                        </div>
                    </div>
                </div>
            )}

            {/* TRANSFER DIALOG */}
            <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Transférer de département</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-4" data-testid="user-transfer-dialog">
                        <div><Label className="form-label text-gray-500">Collaborateur</Label><p className="text-sm font-medium">{transferUser?.first_name} {transferUser?.last_name}</p></div>
                        <div><Label className="form-label text-gray-500">Département actuel</Label><p className="text-sm">{transferUser ? (departments.find((d) => d.id === transferUser.department_id)?.name || "—") : "—"}</p></div>
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <Label className="form-label">Nouveau département *</Label>
                                <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
                                    <input type="checkbox" checked={transferFreeText} onChange={(e) => { setTransferFreeText(e.target.checked); setTransferToDeptId(""); setTransferToDeptName(""); }} className="w-3 h-3" />Saisie libre
                                </label>
                            </div>
                            {transferFreeText ? (
                                <Input value={transferToDeptName} onChange={(e) => { setTransferToDeptName(e.target.value); setTransferToDeptId(""); }} placeholder="Nom du département..." data-testid="user-transfer-dept-free" />
                            ) : (
                                <Select value={transferToDeptId || "none"} onValueChange={handleSelectTransferDept}>
                                    <SelectTrigger data-testid="user-transfer-dept-select"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Sélectionner...</SelectItem>
                                        {departments.filter((d) => d.id !== transferUser?.department_id).map((d) => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>
                        <div><Label className="form-label">Motif</Label><Input value={transferReason} onChange={(e) => setTransferReason(e.target.value)} placeholder="Réorganisation, promotion..." data-testid="user-transfer-reason" /></div>
                        <div><Label className="form-label">Date effective</Label><Input type="date" value={transferDate} onChange={(e) => setTransferDate(e.target.value)} data-testid="user-transfer-date" /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setTransferOpen(false)}>Annuler</Button>
                        <Button onClick={handleTransferSubmit} disabled={transferSubmitting || !transferToDeptName} data-testid="user-confirm-transfer-btn">{transferSubmitting ? "Création..." : "Confirmer le transfert"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
